import { RefObject, useEffect, useId, useMemo } from "react"
import { updateMouse } from "./updateMouse"
import { useAppSelector } from "integrations/redux/hooks"
import { selectActivatedFireId } from "features/map/control/selectors"
import { selectUserID } from "user/profile/selectors"
import { throttle } from "nodash"

interface UseCursorProviderOptions {
    changeFlag?: boolean
    scale?: number
}

export const useCursorTracking = (id: string, { scale = 1 }: UseCursorProviderOptions = {}) => {
    const projectId = useAppSelector(selectActivatedFireId)
    const userId = useAppSelector(selectUserID)
    useUpdateCursor(id)

    const onMouseMove = useMemo(
        () =>
            throttle(
                (pos: MousePosition) =>
                    projectId &&
                    userId &&
                    pos.id === id &&
                    updateMouse({
                        mouse: {
                            coordinates: [pos.x / scale, pos.y / scale],
                            id: pos.id,
                        },
                        projectId,
                        userId,
                    }),
                10
            ),
        [id, projectId, scale, userId]
    )

    useCursorRef((pos) => onMouseMove(pos))
}
interface MousePosition {
    x: number
    y: number
    id: string
}

type Listener = (pos: MousePosition) => void

const cursorRef = {
    current: { x: 0, y: 0, id: "root" },
} satisfies RefObject<MousePosition | null>

const listeners = new Map<string, Listener>()
const trackedRefs = new Set<string>()

const useUpdateCursor = (id: string) => {
    useEffect(() => {
        trackedRefs.add(id)

        const mouseMoveListener = (e: MouseEvent) => {
            const elements = document.elementsFromPoint(e.clientX, e.clientY)
            for (const el of elements) {
                for (const trackedId of trackedRefs.values()) {
                    if (el.id === trackedId) {
                        const rect = el.getBoundingClientRect()
                        const pos: MousePosition = {
                            x: e.clientX - rect.left,
                            y: e.clientY - rect.top,
                            id: trackedId,
                        }

                        cursorRef.current = pos
                        listeners.forEach((cb) => cb(pos))
                        return
                    }
                }
            }
        }

        document.addEventListener("mousemove", mouseMoveListener)

        return () => {
            trackedRefs.delete(id)
            document.removeEventListener("mousemove", mouseMoveListener)
        }
    }, [id])
}

const useCursorRef = (listener?: Listener): RefObject<MousePosition | null> => {
    const id = useId()

    useEffect(() => {
        if (!listener) return
        listeners.set(id, listener)
        return () => {
            listeners.delete(id)
        }
    }, [id, listener])

    return cursorRef
}

export { useUpdateCursor as useUpdateCursorRef, useCursorRef }
